<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        label="Classificação"
                        v-model="model.classificacaoId"
                        text-field="descricao"
                        value-field="id"
                        :validations="validations.classificacao"
                        :options="form.classificacoes"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :options="form.subclassificacoes"
                        :validations="validations.subclassificacao"
                        @change="handleSubclassificacaoChange"
                        label="Subclassificação"
                        show-default-option
                        text-field="descricao"
                        v-model="model.subclassificacaoId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-date-picker
                        :disabled="enums.subclassificacao.TEMPORARIO !== model.subclassificacaoId"
                        :range-inline="false"
                        end-label="Até"
                        range
                        right
                        start-label="Válido de"
                        v-model="model.periodoValidade"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-input
                        label="Prontuário municipal"
                        v-model="form.prontuario"
                        disabled
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        label="Prontuário da unidade"
                        v-model="model.prontuarioUnidade"
                        :validations="validations.prontuarioUnidade"
                        uppercase
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        label="Unidade de referência"
                        v-model="model.unidadeId"
                        :validations="validations.unidadeReferencia"
                        :options="form.unidadesReferencia"
                        text-field="nomeFantasia"
                        value-field="id"
                        show-default-option
                        @change="handleUnidade()"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :options="form.bloqueado"
                        label="Cadastro bloqueado"
                        text-field="text"
                        v-model="model.bloqueado"
                        value-field="value"
                        :disabled="!form.isPermissaoBloquear"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-area
                type="section"
                title="Profissional responsável pelo cadastro"
                no-margin-top
            >

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.usuario"
                            :requestQueryStringParams="autocompleteRequestParams.usuario"
                            :searchFields="fields.autocomplete.usuario"
                            text-field="cidadaoNome"
                            label="Profissional"
                            placeholder="Digite o nome ou número do conselho profissional"
                            v-model="model.responsavelUsuario"
                            uppercase
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="unidades"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.responsavelUnidadeId"
                            :validations="validations.responsavelUnidade"
                            default-option-text="Selecione"
                            value-field="id"
                            :disabled="model.responsavelUsuario && model.responsavelUsuario.id ? false : true"
                            @change="handleUnidade()"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="infraestruturas"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.responsavelInfraestruturaId"
                            :validations="validations.responsavelInfraestruturaId"
                            default-option-text="Selecione"
                            value-field="id"
                            :disabled="model.responsavelUnidadeId == null"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            label="Código da equipe"
                            :options="equipes"
                            textField="descricao"
                            v-model="model.responsavelEquipeId"
                            :validations="validations.equipe"
                            valueField="id"
                            show-default-option
                            :disabled="model.responsavelUnidadeId == null"
                            @change="handleEquipe(false)"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            label="Ocupação"
                            :options="ocupacoes"
                            textField="codigoNome"
                            v-model="model.responsavelOcupacaoId"
                            :validations="validations.ocupacao"
                            valueField="id"
                            show-default-option
                            :disabled="model.responsavelUnidadeId == null"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-col>
                            <rkm-form-date-picker
                                label="Data"
                                :max-date="form.dataAtual"
                                v-model="model.responsavelData"
                                :validations="validations.data"
                                :disabled="true"
                            />
                        </rkm-col>
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </rkm-form>

        <ListPaciente
            v-if="model.prontuariosUnidades"
            :items="model.prontuariosUnidades"
        />
    </div>
</template>

<script>
// Moment.
import moment from 'moment'

// Components.
import ListPaciente from './ListPaciente'

// Enums.
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import permissao from '@/enums/atencao-basica/parametrizacao/permissao'
import tipoAtendimento from '@/enums/atencao-basica/atendimento-individual/tipoAtendimento'

// Vuex.
import { mapGetters, mapActions } from 'vuex'

// Mixins
import { repository, object } from '@/mixins'

export default {
    components: { ListPaciente },
    mixins: [ repository, object ],
    props: {
        form: { type: Object, required: true },
        model: { type: Object, required: true },
    },
    created() {
        this.repositories = {
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            atendimentoIndividual: this.getConectaSusRepository('atencaoBasica.atendimentoIndividual')
        }
    },
    data: () => ({
        enums: {
            classificacao,
            subclassificacao,
            permissao,
            tipoAtendimento
        },
        unidades: [],
        equipes: [],
        infraestruturas: [],
        ocupacoes: [],
    }),
    computed: {
        ...mapGetters({ cliente: 'getCliente' }),
        validations() {

            return {
                classificacao: { required: true },
                subclassificacao: {},
                prontuarioUnidade: { max: 10 },
                unidadeReferencia: { required: true },
                responsavelUnidade: { required: true },
                usuario: { required: true },
                infraestrutura: { required: true },
                equipe: { required: true },
                ocupacao: { required: true },
                data: { required: true },
            }
        },
        autocompleteRequestParams() {
            return {
                usuario: {
                    with_not_regularized: true,
                    with_atencao_basica_equipe: true,
                },
            }
        },
        fields() {
            return {
                autocomplete: {
                    usuario: [
                        { key: "cadastroCidadao.nome", searchable: true },
                        { key: "cadastroUsuario.registroNumero", searchable: true },
                    ],
                    cid: [{ key: "nome", searchable: true }],
                },
            }
        },
    },
    mounted () {

        this.$nextTick().then(() => {

            if (this.model.responsavelUsuario && this.model.responsavelUsuario.id) {

                this.getUnidades()

                if (this.model.responsavelUnidadeId) {

                    this.getInfraestruturas()
                    this.getEquipes()
                    this.getOcupacoes()
                }
            }
        }).then(() => {

            this.addWatchData()
        })
    },
    watch: {
        'form.dataAtual': function (dataAtual) {

            if (dataAtual && !this.model.responsavelData) {

                this.model.responsavelData = moment(dataAtual, 'DD/MM/YYYY').toDate()
            }
        },
        'model.classificacaoId': function (currentId, previousId) {

            this.$emit('select-classificacao', currentId)

            if (this.form.exibeConfirmacaoClassificacao
                && currentId
                && currentId !== previousId
            ) {

                this.showConfirmation(
                    this.getMessageClassificacao(currentId),
                    async () => {},
                    async () => this.model.classificacaoId = previousId
                )
            }
        },
        'model.unidadeId': function (unidadeId) {

            if (unidadeId) {

                this.handleUnidade()
            }
        },
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        validate() {

            return this.$refs.Form.validate()
        },
        getMessageClassificacao(id) {

            let message = ''

            switch (id) {

                case this.enums.classificacao.MUNICIPE: {

                    message = `${this.cliente.municipio.nome} com comprovação de endereço`

                    break;
                }
                case this.enums.classificacao.PROVISORIO: {

                    message = `${this.cliente.municipio.nome} sem comprovação de endereço`

                    break;
                }
                case this.enums.classificacao.OUTRO_MUNICIPIO: {

                    message = 'outro município'

                    break;
                }
            }

            return `Este paciente reside em ${message}. Confirma o cadastro?`
        },
        handleSubclassificacaoChange() {

            this.model.periodoValidade.start = null
            this.model.periodoValidade.end = null
        },
        getUnidades() {

            if (this.model.responsavelUsuario.id) {

                this.setLoading()

                this.repositories.usuario.getUnidades({usuario_id: this.model.responsavelUsuario.id})
                    .then(({ data }) => {

                        this.unidades = data

                        if (data.length === 1){

                            this.model.responsavelUnidadeId = data[0].id
                        }
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        getInfraestruturas() {

            this.setLoading()

            this.repositories.usuario.getInfraestruturas({
                usuario_id: this.model.responsavelUsuario.id,
                unidade_id: this.model.responsavelUnidadeId
            })
                .then(({ data }) => {

                    this.infraestruturas = data

                    if (data.length === 1) {

                        this.model.responsavelInfraestruturaId = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
            },
        getEquipes() {

            this.setLoading()

            this.repositories.usuario.getEquipes({
                usuario_id: this.model.responsavelUsuario.id,
                infraestrutura_id: this.model.responsavelInfraestruturaId
            })
                .then(({ data }) => {

                    this.equipes = data

                    if (data.length === 1){

                        this.model.responsavelEquipeId = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getOcupacoes() {

            this.setLoading()

            this.repositories.usuario.getOcupacoes({
                usuario_id: this.model.responsavelUsuario.id,
                infraestrutura_id: this.model.responsavelInfraestruturaId
            })
                .then(({ data }) => {

                    this.ocupacoes = data

                    if (data.length === 1){

                        this.model.responsavelOcupacaoId = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        setCadastro(field) {

            if (field === 'usuario'){

                this.model.responsavelUnidadeId = null
                this.model.responsavelEquipeId = null
                this.model.responsavelOcupacaoId = null
            } else if (field === 'infraestrutura'){

                this.model.responsavelEquipeId = null
                this.model.responsavelOcupacaoId = null
            } else {

                this.model.responsavelInfraestruturaId = null
                this.model.responsavelEquipeId = null
                this.model.responsavelOcupacaoId = null
            }
        },
        handleUnidade() {

            if (this.model.unidadeId && this.model.responsavelUnidadeId
                && this.model.unidadeId !== this.model.responsavelUnidadeId) {

                const unidadeReferencia = this.form.unidadesReferencia.find(u => u.id === this.model.unidadeId)
                const unidadeResponsavel = this.unidades.find(u => u.id === this.model.responsavelUnidadeId)

                if (unidadeReferencia && unidadeResponsavel) {

                    this.showAlert(`A unidade de referência <strong>(${unidadeReferencia.nomeFantasia})</strong> é diferente da unidade
                        <strong>(${unidadeResponsavel.nomeFantasia})</strong> responsável pelo cadastro.`, "Atenção")
                }
            }
        },
        handleEquipe(value) {

            if (this.model.responsavelEquipeId && this.model.microareaId) {

                let msg = ''

                if (value && this.model.unidadeId && this.model.responsavelUnidadeId
                    && this.model.unidadeId !== this.model.responsavelUnidadeId) {

                    const unidadeReferencia = this.form.unidadesReferencia.find(u => u.id === this.model.unidadeId)
                    const unidadeResponsavel = this.unidades.find(u => u.id === this.model.responsavelUnidadeId)

                    msg = msg.concat(`A unidade de referência <strong>(${unidadeReferencia.nomeFantasia})</strong> é diferente da unidade
                        <strong>(${unidadeResponsavel.nomeFantasia})</strong> responsável pelo cadastro. <br><br>`)
                }

                const equipe = this.equipes.find(u => u.id === this.model.responsavelEquipeId)
                const microarea = this.form.microareas.find(u => u.id === this.model.microareaId)

                if (equipe.microareaId !== microarea.id) {

                    msg = msg.concat(`A Área/Microárea <strong>(${equipe.descricao})</strong> da equipe responsável é diferente da
                        Área/Microárea <strong>(${microarea.descricao})</strong> do domicílio.<br><br>Deseja vincular a área/microárea
                        da equipe à área/microárea do domicílio?`)

                        this.showConfirmation(msg, async () => this.handleMicroarea(equipe))
                }
            }
        },
        handleMicroarea(equipe) {

            this.model.microareaId = equipe.microareaId
        },
        limparResponsavel() {
            this.model.responsavelUsuario = null
            this.model.responsavelUnidadeId = null
            this.model.responsavelInfraestruturaId = null
            this.model.responsavelEquipeId = null
            this.model.responsavelOcupacaoId = null
        },
        addWatchData() {

            this.$watch('model.responsavelUsuario', function (usuario) {

                if (usuario) {

                    this.getUnidades()
                } else {

                    this.setCadastro('usuario')
                }
            })

            this.$watch('model.responsavelUnidadeId', function (unidadeId) {

                if (unidadeId) {

                    this.getInfraestruturas()
                    this.handleUnidade()
                }

                this.setCadastro('unidade')
            })

            this.$watch('model.responsavelInfraestruturaId', function (infraestruturaId) {

                if (infraestruturaId) {

                    this.getEquipes()
                    this.getOcupacoes()
                }

                this.setCadastro('infraestrutura')
            })

            this.$watch('model.responsavelEquipeId', function (responsavelEquipeId) {

                if (responsavelEquipeId) {

                    this.handleEquipe(true)
                }
            })
        },
    },
}
</script>